/* eslint @typescript-eslint/no-explicit-any: 0 */

import { useAuth } from "../contexts/auth.context";
import { signinUser } from "../../features/api/userSlice";
import React, { useState } from "react";
import {
    Link,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import HomeIcon from "@mui/icons-material/Home";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, Navigate } from "react-router-dom";

const SignIn: React.FC = () => {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleSignIn = async () => {
        try {
            setError("Signing in...");
            const user = await signinUser({ email, password }, rememberMe);
            setUser(user);
            navigate(-1);
        } catch (error: any) {
            console.log(error);
            if (error.response.status === 401) {
                setError("Invalid email or password.");
            } else {
                setError("Failed to sign in, please try again.");
            }
        }
    };

    if (user.id !== -1) {
        return <Navigate to="/" />;
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100vh"
            sx={{ bgcolor: "#E8FDEA" }}
        >
            <Typography
                variant="h3"
                color="Primary"
                marginBottom={2}
                sx={{
                    textShadow: `
                1px 1px 1px rgba(0, 0, 0, 0.1),
                2px 2px 2px rgba(0, 0, 0, 0.2),
                3px 3px 3px rgba(0, 0, 0, 0.3)`,
                }}
            >
                <b>Sign In</b>
            </Typography>
            <p color="Secondary">No account? Sign up now to enjoy a personalised experince.</p>
            {error && (
                <Typography color="error" marginBottom={2}>
                    {error}
                </Typography>
            )}
            <Stack
                sx={{
                    width: "30ch",
                    marginTop: 3,
                }}
            >
                <TextField
                    required
                    label="Email"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                    required
                    label="Password"
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    margin="normal"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={togglePasswordVisibility}>
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Remember me"
                />
            </Stack>
            <Button
                size="medium"
                variant="contained"
                color="primary"
                endIcon={<SendIcon />}
                onClick={handleSignIn}
                sx={{ marginTop: 3 }}
                disabled={!email || !password}
            >
                Sign In
            </Button>
            <Link href="/forget-password" variant="body2" sx={{ marginTop: 2 }}>
                Forgot Password?
            </Link>
            <Button
                size="medium"
                variant="outlined"
                color="secondary"
                endIcon={<AppRegistrationIcon />}
                onClick={() => navigate("/signup")}
                sx={{ marginTop: 2 }}
            >
                Sign Up
            </Button>
            <Button
                size="large"
                variant="text"
                color="secondary"
                endIcon={<HomeIcon />}
                onClick={() => navigate("/")}
                sx={{ marginTop: 20 }}
            >
                Home
            </Button>
        </Box>
    );
};

export default SignIn;
